import { BoundingSphere, Cartesian3, Cartographic, Cesium3DTileset, ConstantPositionProperty, ConstantProperty, JulianDate, KmlDataSource, Matrix4, Viewer, Math as CesiumMath, OpenStreetMapImageryProvider, ImageryLayer, VerticalOrigin, Color, Cartesian2, Entity, ScreenSpaceEventHandler, ScreenSpaceEventType } from "cesium";
import { IInputObjs3DTile } from "src/interfaces/sceneManager.interface";
import { InputDataPrimitive } from "./styleElement";
import locationPin from '../assets/images/location-pin.png'
import { getMessage } from "../language";
import { TileEdgeRenderer, EdgeRenderingOptions } from "./scene/tileEdgeRenderer";
import { EMapUrl } from "../enums/map.enum";


const getDeviceCacheSettings = () => {
    // Kiểm tra xem có phải thiết bị di động hay không
    const isMobile = typeof navigator !== 'undefined' && (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        || (typeof window !== 'undefined' && window.matchMedia("(pointer: coarse)").matches));

    // Lấy thông tin dung lượng RAM ước tính (mặc định 4GB nếu không lấy được)
    const ramGB = typeof navigator !== 'undefined' ? ((navigator as any).deviceMemory || 4) : 4;

    // Lấy số luồng CPU (mặc định 4 nếu không lấy được)
    const cpuCores = typeof navigator !== 'undefined' ? (navigator.hardwareConcurrency || 4) : 4;

    if (isMobile || ramGB <= 2 || cpuCores <= 2) {
        // Thiết bị cấu hình thấp hoặc Mobile
        return { cacheMB: 256, overflowMB: 256 };
    } else if (ramGB <= 4 || cpuCores <= 4) {
        // Thiết bị cấu hình trung bình
        return { cacheMB: 512, overflowMB: 512 };
    } else {
        // Thiết bị cấu hình mạnh
        return { cacheMB: 1024, overflowMB: 1024 };
    }
};

const cacheSettings = getDeviceCacheSettings();
const CATCH_MB = cacheSettings.cacheMB;
const OVERFLOW_MB = cacheSettings.overflowMB;

export class SceneManager {
    viewer: Viewer;
    private _urlApi = `https://translateservicedev.corebim.com/manageTranslateApi`
    private _token = ''
    private _inputDataPrimitive: InputDataPrimitive
    /** Quản lý hiển thị cạnh (edge/wireframe) cho 3D tiles */
    private _edgeRenderer: TileEdgeRenderer
    private _tilesetsLoadedByUrl: Cesium3DTileset[] = []
    private _savemaximumScreenSpaceErrors = new Map<any, number>()
    private _kmzClickHandler?: ScreenSpaceEventHandler;
    constructor(containerId: string, viewer: any, token: string, inputDataPrimitive: InputDataPrimitive) {
        this.viewer = viewer;//Gán giá trị viewer
        this._token = token
        this._inputDataPrimitive = inputDataPrimitive
        this._edgeRenderer = new TileEdgeRenderer(viewer)
        this.enableEdgeRendering({
            color: Color.BLACK,
            strength: 4,        // 2–10: cạnh giữa mặt phẳng
            depthStrength: 1,  // 10–60: viền ngoài silhouette (mặc định tắt bằng 0)
            thickness: 0.2,
            opacity: 0.5
        });

        // Tinh chỉnh nếu cần
        this.setEdgeStrength(4);       // tăng nếu chưa thấy rõ cạnh
        this.setEdgeDepthStrength(2); // Bật mặc định silhouette để vẽ viền bao ngoài sắc nét
        this.setEdgeThickness(0.2);   // dày hơn nếu cạnh quá mảnh

        // Đăng ký listener duy nhất cho camera
        this.viewer.camera.moveStart.addEventListener(this._handleCameraMoveStart);
        this.viewer.camera.moveEnd.addEventListener(this._handleCameraMoveEnd);
    }

    //#region load3DTileUrl
    /**
  * Hàm load model từ url JSON
  * @param {string} url URL Json model
  * @param {number} maximumScreen 
  */
    load3DTileUrl = async (url: string, inputObjs?: IInputObjs3DTile) => {
        let tileset
        try {
            const csTileset = await Cesium3DTileset.fromUrl(url,
                {
                    debugShowBoundingVolume: false,
                    maximumScreenSpaceError: inputObjs?.maximumScreen ?? 16,

                },
            )
            try { csTileset.cacheBytes = Number(CATCH_MB) * 1048576; } catch { }
            try { csTileset.maximumCacheOverflowBytes = Number(OVERFLOW_MB) * 1048576; } catch { }

            const pcs = csTileset.pointCloudShading;
            // Bật/tắt Eye Dome Lighting (EDL). Nếu bật (true) sẽ tạo viền đổ bóng (depth shading) 
            // xung quanh các điểm giúp nhìn rõ hình khối 3D hơn, nhưng sẽ tốn tài nguyên GPU hơn.
            pcs.eyeDomeLighting = false;

            // Độ đậm/nhạt của viền đổ bóng EDL (chỉ có tác dụng khi eyeDomeLighting = true).
            pcs.eyeDomeLightingStrength = 1.0;

            // Bán kính viền đổ bóng EDL (tính bằng pixel, chỉ có tác dụng khi eyeDomeLighting = true).
            pcs.eyeDomeLightingRadius = 1.0;

            // Bật/tắt tính năng tự động phóng to/thu nhỏ kích thước điểm (Point Attenuation) 
            // dựa trên khoảng cách camera và sai số hình học (geometric error). 
            // Nếu tắt (false), các điểm sẽ luôn có kích thước cố định 1 pixel giúp tăng hiệu năng cực tốt.
            pcs.attenuation = true;

            // // Hệ số nhân tỷ lệ sai số hình học để tính toán attenuation (chỉ có tác dụng khi attenuation = true).
            pcs.geometricErrorScale = 1;

            // Kích thước tối đa của điểm (tính bằng pixel) khi camera tiến sát lại gần (chỉ có tác dụng khi attenuation = true).
            pcs.maximumAttenuation = inputObjs?.sizePointCloud ?? 2;

            tileset = this.viewer.scene.primitives.add(
                csTileset
            );

        } catch (error) {
            console.error("Link lỗi:", url);
            console.error("Báo lỗi:", error);
        }

        if (tileset) {
            this._tilesetsLoadedByUrl.push(tileset);
            this.updateEdgeRendererSelected();


        }

        return tileset;
    }
    //#region setPositionModel
    setPositionModel = (tileset: any, inputObjs?: IInputObjs3DTile) => {
        if (tileset) {
            if (inputObjs?.longitude || inputObjs?.latitude || inputObjs?.height) {
                if (tileset?.entities) {
                    this.setModelMatrixForKmz(tileset, inputObjs.longitude, inputObjs.latitude, inputObjs.height)
                } else {

                    this.setModelMatrix(tileset, inputObjs.longitude, inputObjs.latitude, inputObjs.height)
                }

            }
            if (inputObjs?.z) {
                this.transformZ(tileset, inputObjs.z)
            }
        }
    }

    //#region loadKmz
    /**
        * Hàm load file KMZ
        * @param {string} url URL KMZ
        */
    loadKmz = async (path: string, inputObjs?: IInputObjs3DTile) => {

        let kmz = await this.viewer.dataSources.add(KmlDataSource.load(path,
            {
                camera: this.viewer.scene.camera,
                canvas: this.viewer.scene.canvas,
            })
        );
        if (kmz) {
            if (inputObjs?.longitude
                || inputObjs?.latitude || inputObjs?.height) {
                this.setModelMatrixForKmz(kmz, inputObjs.longitude, inputObjs.latitude, inputObjs.height)
            }

            if (inputObjs?.z) {
                this.transformZ(kmz, inputObjs.z)

            }
        }

        // // if (kmz) {
        // //     const pixelRange = 100;
        // //     const minimumClusterSize = 15;
        // //     const enabled = true;

        // //     kmz.clustering.enabled = enabled;
        // //     kmz.clustering.pixelRange = pixelRange;
        // //     kmz.clustering.minimumClusterSize = minimumClusterSize;

        // //     //Bật hiển ẩn hiện theo chiều dài của KMZ
        // //     kmz.clustering.clusterEvent.addEventListener((clusteredEntities: any, cluster: any) => {
        // //         cluster.billboard.show = false;
        // //         cluster.label.show = false;
        // //         console.log("clusteredEntities",clusteredEntities);
        // //         console.log("cluster",cluster);
        // //         // Lấy các tham số cần thiết
        // //         const heightCamera = kmz.camera.positionCartographic.height;
        // //         console.log("heightCamera", heightCamera);

        // //     })
        //     // kmz.clustering.pixelRange = pixelRange;



        // }


        // console.log("kmz", kmz);

        return kmz;
    }
    //#region removeKmz
    removeKmz = (kmz: any) => {
        this.viewer.dataSources.remove(kmz, true);
    }


    //#region load3DTileById
    load3DTileById = async (idData: string, urlConnect?: string, inputObjs?: IInputObjs3DTile) => {
        let tileSet
        let dataConnect
        const response = await fetch(
            `${this._urlApi}/Translate/GetFileTileSet?translateId=${idData}`,
            {
                method: "GET",
                headers: {
                    "accept": "*/*",
                    "Authorization": `Bearer ${this._token}`,
                },
            }
        );
        if (!response.ok) {
            throw new Error(`API trả về lỗi: ${response.status} - ${response.statusText}`);
        }

        const result = await response.json();
        if (result.statusCode == 200) {
            tileSet = await this.load3DTileUrl(result.data.tileSet, inputObjs)
            await this._inputDataPrimitive!.setObjDataPrimitive(result.data.tileSet)

            if (result.data.connectFile) {

                dataConnect = await this._inputDataPrimitive!.setInputObjDataPrimitive(result.data.connectFile, result.data.tileSet)
            }
            if (urlConnect) {
                dataConnect = await this._inputDataPrimitive!.setInputObjDataPrimitive(urlConnect, result.data.tileSet)
            } else {

            }


        }
        return { tileSet: tileSet, url: result.data.tileSet, dataConnect }
    }


    showHideTile = (tile: any, show: boolean) => {
        if (show) {
            tile.show = true;
        } else {
            tile.show = false;
        }
    }


    /**
    * Điều hướng camera đến tileset khi tileset đã được tải
    * @param {any} tileset tile được load
    */
    zoomTo = (tileset: any) => {

        this.viewer.zoomTo(tileset);

    }


    /**
    * Hàm bay đến bounding Box
    * @param {BoundingSphere} bouding bounding Box
    */
    cameraFlyToBouding = (bouding: BoundingSphere, duration = 0) => {
        this.viewer.camera.flyToBoundingSphere(bouding, {
            duration: duration
        })

    }


    removeTileSet = (tileset: any) => {
        this.viewer.scene.primitives.remove(tileset)
        this._tilesetsLoadedByUrl = this._tilesetsLoadedByUrl.filter(t => t !== tileset);
        this._savemaximumScreenSpaceErrors.delete(tileset);
        this.updateEdgeRendererSelected();
    }

    //#region transformZ
    transformZ = (tileset: any, heightOffset: number, cartographic?: any) => {

        if (tileset?.boundingSphere) {
            const boundingSphere = tileset.boundingSphere;
            let cartographicBound = Cartographic.fromCartesian(boundingSphere.center);
            if (cartographic?.longitude !== undefined && cartographic?.latitude !== undefined && cartographic?.height !== undefined) {
                cartographicBound = cartographic

            }

            const surface = Cartesian3.fromRadians(cartographicBound.longitude, cartographicBound.latitude, 0.0);
            const offset = Cartesian3.fromRadians(cartographicBound.longitude, cartographicBound.latitude, heightOffset);

            const translation = Cartesian3.subtract(offset, surface, new Cartesian3());
            tileset.modelMatrix = Matrix4.fromTranslation(translation);
        }
        else if (tileset?.entities) {
            tileset.entities.values.forEach((entity: any) => {
                if (entity.position) {
                    // Nếu thực thể có thuộc tính `position`
                    const originalPosition = entity.position.getValue(JulianDate.now());
                    const cartographic = Cartographic.fromCartesian(originalPosition);

                    // Thêm độ mét vào cao độ
                    cartographic.height += heightOffset;
                    const newPosition = Cartesian3.fromRadians(
                        cartographic.longitude,
                        cartographic.latitude,
                        cartographic.height
                    );

                    // Cập nhật vị trí mới
                    entity.position = new ConstantPositionProperty(newPosition);
                } else if (entity.polyline) {
                    // Nếu thực thể là một `Polyline`, nâng cao tất cả các điểm của polyline
                    const positions = entity.polyline.positions.getValue(JulianDate.now()).map((pos: any) => {
                        const cartographic = Cartographic.fromCartesian(pos);
                        cartographic.height += heightOffset;
                        return Cartesian3.fromRadians(
                            cartographic.longitude,
                            cartographic.latitude,
                            cartographic.height
                        );
                    });
                    entity.polyline.positions = new ConstantProperty(positions);
                } else if (entity.polygon) {
                    // Nếu thực thể là một `Polygon`, nâng cao tất cả các điểm của polygon
                    const hierarchy = entity.polygon.hierarchy.getValue(JulianDate.now());
                    const positions = hierarchy.positions.map((pos: any) => {
                        const cartographic = Cartographic.fromCartesian(pos);
                        cartographic.height += heightOffset;
                        return Cartesian3.fromRadians(
                            cartographic.longitude,
                            cartographic.latitude,
                            cartographic.height
                        );
                    });
                    entity.polygon.hierarchy = new ConstantProperty({ positions });
                }
            });
        }
    }
    //#region getCurrentCartographic
    getCurrentCartographic = (tileset: any) => {
        if (tileset?.entities) {
            const entities = tileset.entities.values;
            const currentCenter = this._computeCenterOfEntities(entities);
            const currentCartographic = Cartographic.fromCartesian(currentCenter!);
            return currentCartographic
        } else {

            const boundingSphere = tileset.boundingSphere;
            const currentCenter = boundingSphere?.center;
            const currentCartographic = Cartographic.fromCartesian(currentCenter);
            return currentCartographic
        }
        return undefined
    }

    //#region setModelMatrix
    setModelMatrix = (tileset: any, longitude?: number, latitude?: number, height?: number, cartographic?: any) => {

        // Nếu chưa lưu originalMatrix thì clone
        if (!tileset._originalModelMatrix) {
            tileset._originalModelMatrix = Matrix4.clone(tileset.modelMatrix);
        }

        // Reset modelMatrix về gốc
        tileset.modelMatrix = Matrix4.clone(tileset._originalModelMatrix);

        // Tính vị trí hiện tại
        const boundingSphere = tileset.boundingSphere;
        const currentCenter = boundingSphere?.center;
        const currentCartographic = Cartographic.fromCartesian(currentCenter);
        let newLongitude = longitude !== undefined ? CesiumMath.toRadians(longitude!) : currentCartographic.longitude;
        let newLatitude = latitude !== undefined ? CesiumMath.toRadians(latitude!) : currentCartographic.latitude;
        let newHeight = height !== undefined ? height : currentCartographic.height;

        if (cartographic?.longitude !== undefined && cartographic?.latitude !== undefined && cartographic?.height !== undefined) {
            newLongitude = longitude !== 0 ? CesiumMath.toRadians(longitude!) : cartographic.longitude;
            newLatitude = latitude !== 0 ? CesiumMath.toRadians(latitude!) : cartographic.latitude;
            newHeight = height !== 0 ? height : cartographic.height;

        }

        const newCartographic = new Cartographic(newLongitude, newLatitude, newHeight);
        const newPosition = Cartographic.toCartesian(newCartographic);

        // Tính delta di chuyển
        const translation = Cartesian3.subtract(newPosition, currentCenter, new Cartesian3());

        const translationMatrix = Matrix4.fromTranslation(translation);
        // Apply dịch chuyển

        tileset.modelMatrix = Matrix4.multiply(tileset.modelMatrix, translationMatrix, new Matrix4());



    }

    //#region  setModelMatrix cho KMZ (entities)
    setModelMatrixForKmz = (dataSource: any, targetLongitude?: number, targetLatitude?: number, targetHeight?: number) => {
        if (!dataSource.entities) return;

        const entities = dataSource.entities.values;

        const currentCenter = this._computeCenterOfEntities(entities);
        if (!currentCenter) return;

        const targetCartesian = Cartesian3.fromDegrees(targetLongitude || 0, targetLatitude || 0, targetHeight || 0);

        const delta = Cartesian3.subtract(targetCartesian, currentCenter, new Cartesian3());

        this._moveEntitiesByDelta(entities, delta);
    }


    private _computeCenterOfEntities = (entities: any[]) => {
        let positions: Cartesian3[] = [];

        entities.forEach((entity) => {
            const julianDate = JulianDate.now();
            if (entity.position) {
                const pos = entity.position.getValue(julianDate);
                if (pos) positions.push(pos);
            } else if (entity.polyline && entity.polyline.positions) {
                const posList = entity.polyline.positions.getValue(julianDate) || [];
                positions.push(...posList);
            } else if (entity.polygon && entity.polygon.hierarchy) {
                const hierarchy = entity.polygon.hierarchy.getValue(julianDate);
                if (hierarchy && hierarchy.positions) {
                    positions.push(...hierarchy.positions);
                }
            }
        });

        if (positions.length === 0) return undefined;

        let center = new Cartesian3(0, 0, 0);
        positions.forEach((pos) => {
            Cartesian3.add(center, pos, center);
        });

        Cartesian3.divideByScalar(center, positions.length, center);
        return center;
    }

    private _moveEntitiesByDelta = (entities: any[], delta: Cartesian3) => {
        entities.forEach((entity) => {
            const julianDate = JulianDate.now();
            if (entity.position) {
                const pos = entity.position.getValue(julianDate);
                if (pos) {
                    const newPos = Cartesian3.add(pos, delta, new Cartesian3());
                    entity.position = new ConstantPositionProperty(newPos);
                }
            } else if (entity.polyline && entity.polyline.positions) {
                const positions = entity.polyline.positions.getValue(julianDate) || [];
                const newPositions = positions.map((pos: any) => {
                    return Cartesian3.add(pos, delta, new Cartesian3());
                });
                entity.polyline.positions = new ConstantProperty(newPositions);
            } else if (entity.polygon && entity.polygon.hierarchy) {
                const hierarchy = entity.polygon.hierarchy.getValue(julianDate);
                if (hierarchy && hierarchy.positions) {
                    const newPositions = hierarchy.positions.map((pos: any) => {
                        return Cartesian3.add(pos, delta, new Cartesian3());
                    });
                    entity.polygon.hierarchy = new ConstantProperty({ positions: newPositions });
                }
            }
        });
    }
    //#region getBoundingBoxFromKMZ
    getBoundingBoxFromKMZ = (kmlDataSource: any) => {
        let minX = Infinity, minY = Infinity, minZ = Infinity;
        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

        const entities = kmlDataSource.entities.values;
        // Lặp qua các entities để tính min/max cho x, y, z
        entities.forEach((entity: any) => {
            // Xử lý entity.position (Cartesian3)
            if (entity.position) {
                const position = entity.position.getValue(JulianDate.now());

                // Lấy các giá trị x, y, z từ Cartesian3
                const x = position.x;
                const y = position.y;
                const z = position.z;

                // Tính min/max cho mỗi trục
                minX = Math.min(minX, x);
                maxX = Math.max(maxX, x);
                minY = Math.min(minY, y);
                maxY = Math.max(maxY, y);
                minZ = Math.min(minZ, z);
                maxZ = Math.max(maxZ, z);
            }

            // Xử lý entity.polyline.positions (Cartesian3)
            if (entity.polyline && entity.polyline.positions) {
                const positions = entity.polyline.positions.getValue(JulianDate.now());
                positions.forEach((position: any) => {
                    const x = position.x;
                    const y = position.y;
                    const z = position.z;

                    minX = Math.min(minX, x);
                    maxX = Math.max(maxX, x);
                    minY = Math.min(minY, y);
                    maxY = Math.max(maxY, y);
                    minZ = Math.min(minZ, z);
                    maxZ = Math.max(maxZ, z);
                });
            }

            // Xử lý entity.polygon.hierarchy (Cartesian3)
            if (entity.polygon && entity.polygon.hierarchy) {
                const positions = entity.polygon.hierarchy.getValue(JulianDate.now()).positions;
                positions.forEach((position: any) => {
                    const x = position.x;
                    const y = position.y;
                    const z = position.z;

                    minX = Math.min(minX, x);
                    maxX = Math.max(maxX, x);
                    minY = Math.min(minY, y);
                    maxY = Math.max(maxY, y);
                    minZ = Math.min(minZ, z);
                    maxZ = Math.max(maxZ, z);
                });
            }
        });
        // Tính toán trung tâm (center) và bán kính (radius) của BoundingSphere
        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;
        const centerZ = (minZ + maxZ) / 2;

        const centerCartesian = new Cartesian3(centerX, centerY, centerZ);

        // Tính bán kính: khoảng cách từ điểm xa nhất đến điểm gần nhất trong BoundingBox
        const distance1 = Cartesian3.distance(
            new Cartesian3(minX, minY, minZ),
            centerCartesian
        );

        const distance2 = Cartesian3.distance(
            new Cartesian3(maxX, maxY, maxZ),
            centerCartesian
        );

        const radius = Math.max(distance1, distance2);

        // Trả về BoundingSphere với center và radius
        return new BoundingSphere(centerCartesian, radius);
    }


    //#region handleZoomEarth
    public handleZoomEarth = (zoom: boolean) => {
        const heightCamera = this.viewer.camera.positionCartographic.height
        const newHeightCam = heightCamera / 2
        if (zoom) {
            this.viewer.camera.zoomIn(newHeightCam)
        } else {
            this.viewer.camera.zoomOut(newHeightCam)
        }
    }

    //#region onChangeMap
    public onChangeMap = (layer: number, mapUrls?: {
        id: string,
        url: string
    }[]) => {
        const defaultLayerMaps: { id: string; url: string }[] = [
            {
                id: "map-road",
                url: EMapUrl.Road
            },
            {
                id: "map-satellite",
                url: EMapUrl.Satellite
            },
        ]
        if (mapUrls && mapUrls.length > 0) {
            defaultLayerMaps.map((layerMap) => {
                const foundMap = mapUrls.find(mapUrl => mapUrl.id === layerMap.id);
                if (foundMap) {
                    layerMap.url = foundMap.url
                } else {
                    defaultLayerMaps.push(layerMap)
                }
            })
        }
        const url = defaultLayerMaps[layer].url
        if (!url) return
        let findUrl = false;
        (this.viewer.scene.imageryLayers as any)._layers.forEach((layer: ImageryLayer) => {
            layer.show = false
            const provider = layer.imageryProvider;
            if (provider instanceof OpenStreetMapImageryProvider) {
                const urlFix = url + "/{z}/{x}/{y}.png"
                if (provider.url === urlFix) {
                    layer.show = true
                    findUrl = true
                }
            }
        })
        if (!findUrl) {
            const layer = new ImageryLayer(new OpenStreetMapImageryProvider({
                maximumLevel: 22,
                url: url
            }));
            layer.show = true;
            this.viewer.scene.imageryLayers.add(layer);

        }

    }
    //#region flyToYourLocation
    flyToYourLocation = (zDistance?: number, duration?: number) => {
        const LABEL_ID = "your-location-marker";
        // Xóa pin location cũ nếu đã tồn tại
        const existingPin = this.viewer.entities.getById("pin-location-marker");
        if (existingPin) {
            this.viewer.entities.remove(existingPin);
        }
        return new Promise((resolve, reject) => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition((position) => {
                    const longitude = position.coords.longitude;
                    const latitude = position.coords.latitude;

                    const entityLabel = this.viewer.entities.getById(LABEL_ID);
                    if (entityLabel) {
                        this.viewer.entities.removeById(LABEL_ID);
                    }
                    this.viewer.entities.add({
                        id: LABEL_ID,
                        position: Cartesian3.fromDegrees(longitude, latitude),
                        billboard: {
                            image: locationPin, // URL trực tuyến cho icon
                            width: 40, // Chiều rộng của icon
                            height: 40, // Chiều cao của icon
                            disableDepthTestDistance: Number.POSITIVE_INFINITY,
                            verticalOrigin: VerticalOrigin.BOTTOM,
                        },
                        label: {
                            text: getMessage('your-location'),
                            font: "14pt sans-serif",
                            verticalOrigin: VerticalOrigin.TOP,
                            showBackground: true,
                            backgroundColor: new Color(0.0, 0.0, 0.0, 0.3), // Màu nền đen với độ trong suốt // Nền màu đỏ
                            pixelOffset: new Cartesian2(0, -70), // Đẩy nhãn lên phía trên icon
                            disableDepthTestDistance: Number.POSITIVE_INFINITY,
                        },
                    })
                    // Fly camera tới vị trí hiện tại
                    this.viewer.camera.flyTo({
                        destination: Cartesian3.fromDegrees(longitude, latitude, zDistance ?? 1000),
                        duration: duration ?? 1,

                        complete: function () {
                            resolve("Fly to location complete and centered");

                        },
                    });
                    return { longitude, latitude };

                }, (error) => {
                    // console.error("Error getting location: ", error);
                    // reject(error);
                    alert(getMessage('alert-noitification-error'))
                })

            }
        });

    }


    enableKmzClickPicking = (callback: (entity: Entity | undefined, dataSource: KmlDataSource | undefined) => void) => {
        this.disableKmzClickPicking();

        this._kmzClickHandler = new ScreenSpaceEventHandler(this.viewer.scene.canvas);

        this._kmzClickHandler.setInputAction((clickEvent: any) => {
            const pickedObject = this.viewer.scene.pick(clickEvent.position);

            if (pickedObject && pickedObject.id instanceof Entity) {
                const entity = pickedObject.id;
                const dataSources = this.viewer.dataSources;
                let found = false;
                for (let i = 0; i < dataSources.length; i++) {
                    const ds = dataSources.get(i);
                    if (ds instanceof KmlDataSource && ds.entities.contains(entity)) {
                        callback(entity, ds);
                        found = true;
                        break;
                    }
                }
                if (!found) {
                    callback(undefined, undefined);
                }
            } else {
                callback(undefined, undefined);
            }
        }, ScreenSpaceEventType.LEFT_CLICK);
    }

    /**
     * Hủy lắng nghe sự kiện click KMZ.
     */
    disableKmzClickPicking = () => {
        if (this._kmzClickHandler && !this._kmzClickHandler.isDestroyed()) {
            this._kmzClickHandler.destroy();
            this._kmzClickHandler = undefined;
        }
    }


    // ─────────────────────────────────────────────────────────────────────
    //  #region Edge Rendering – Hiển thị cạnh / wireframe cho 3D Tiles
    // ─────────────────────────────────────────────────────────────────────

    /**
     * Bật hiển thị cạnh toàn scene bằng **Depth + Normal Sobel Post-Processing**.
     *
     * - **Normal Sobel**: phát hiện cạnh giữa các mặt phẳng (cạnh của box, tường–sàn, v.v.)
     * - **Depth Sobel** : phát hiện viền silhouette bên ngoài vật thể
     *
     * @param options Tùy chọn:
     *   - `strength` (2–10): độ nhạy normal (cạnh mặt phẳng) – mặc định 4
     *   - `depthStrength` (10–60): độ nhạy silhouette – mặc định 20
     *   - `thickness` (1–3): độ dày pixel
     *   - `color`, `opacity`
     *
     * @example
     * ```ts
     * sceneManager.enableEdgeRendering({
     *   color: Color.BLACK,
     *   strength: 4,
     *   depthStrength: 20
     * });
     * ```
     */
    enableEdgeRendering = (options?: EdgeRenderingOptions) => {
        this._edgeRenderer.enable({ mode: 'postprocess', ...options })
    }

    /**
     * Tắt lớp hiển thị cạnh post-process.
     */
    disableEdgeRendering = () => {
        this._edgeRenderer.disablePostProcess()
    }

    /**
     * Bật/Tắt hiển thị cạnh toàn scene.
     */
    toggleEdgeRendering = (options?: EdgeRenderingOptions) => {
        this._edgeRenderer.toggle(options)
    }

    /**
     * Bật hiển thị cạnh trực tiếp **trên một tileset** cụ thể.
     *
     * - `mode: 'shader'`    : Phát hiện cạnh qua `fwidth(normal)` — hoạt động tốt
     *                         với BIM/IFC tiles có flat-shading.
     * - `mode: 'wireframe'` : Hiển thị toàn bộ lưới tam giác (debugWireframe native).
     *
     * @example
     * ```ts
     * // Cạnh bên trong từng face (BIM/IFC models)
     * sceneManager.enableTileEdge(tileset, { mode: 'shader', strength: 18 });
     *
     * // Wireframe toàn bộ tam giác
     * sceneManager.enableTileEdge(tileset, { mode: 'wireframe' });
     * ```
     */
    enableTileEdge = (tileset: Cesium3DTileset, options?: EdgeRenderingOptions) => {
        this._edgeRenderer.enableOnTileset(tileset, { mode: 'shader', ...options })
    }

    /**
     * Tắt hiển thị cạnh trên tileset và khôi phục shader gốc.
     */
    disableTileEdge = (tileset: Cesium3DTileset) => {
        this._edgeRenderer.disableOnTileset(tileset)
    }

    /**
     * Tắt tất cả chế độ edge rendering (toàn scene + từng tileset).
     */
    disableAllEdgeRendering = () => {
        this._edgeRenderer.disable()
    }

    private updateEdgeRendererSelected = () => {
        this._edgeRenderer.setSelectedPrimitives(this._tilesetsLoadedByUrl);
    }

    /** Cập nhật màu cạnh đang hiển thị */
    setEdgeColor = (color: Color) => {
        this._edgeRenderer.setColor(color)
    }

    /** Cập nhật độ nhạy normal-based edge (phát hiện cạnh mặt phẳng, 2–10) */
    setEdgeStrength = (strength: number) => {
        this._edgeRenderer.setStrength(strength)
    }

    /** Cập nhật độ nhạy depth/silhouette edge (10–60) */
    setEdgeDepthStrength = (depthStrength: number) => {
        this._edgeRenderer.setDepthStrength(depthStrength)
    }

    /** Cập nhật độ trong suốt lớp cạnh post-process (0–1) */
    setEdgeOpacity = (opacity: number) => {
        this._edgeRenderer.setOpacity(opacity)
    }

    /** Cập nhật độ dày pixel của cạnh post-process */
    setEdgeThickness = (thickness: number) => {
        this._edgeRenderer.setThickness(thickness)
    }

    /** Trạng thái: đang hiển thị cạnh hay không */
    get isEdgeRenderingEnabled(): boolean {
        return this._edgeRenderer.isEnabled
    }

    private _handleCameraMoveStart = () => {
        this._tilesetsLoadedByUrl.forEach(tileset => {
            if (!this._savemaximumScreenSpaceErrors.has(tileset)) {
                this._savemaximumScreenSpaceErrors.set(tileset, tileset.maximumScreenSpaceError);
            }
            tileset.maximumScreenSpaceError = 64;
        });
    }

    private _handleCameraMoveEnd = () => {
        const trimMB = Number(CATCH_MB + OVERFLOW_MB);
        let hasChanges = false;

        this._tilesetsLoadedByUrl.forEach(tileset => {
            const savedSSE = this._savemaximumScreenSpaceErrors.get(tileset);
            if (savedSSE !== undefined) {
                tileset.maximumScreenSpaceError = savedSSE;
                this._savemaximumScreenSpaceErrors.delete(tileset);
                hasChanges = true;
            }

            if (tileset.totalMemoryUsageInBytes > (trimMB * 1048576)) {
                try {
                    tileset.trimLoadedTiles();
                } catch (e) {
                    console.error("Trim loaded tiles error:", e);
                }
            }
        });

        if (hasChanges && this._tilesetsLoadedByUrl.length > 0) {
            this.viewer.scene.requestRender();
        }
    }

}


